import { vars } from './vars';
import { lang } from '../lang';
import { DateTime, Interval } from 'luxon';

export function formatStartDate(event, includeBR = false) {
    /*
        Cases:
            All day events (startDate & endDate):
                single day: Sunday, Jan 5, 2020
                multi day (same month/year): Jan 5 - 6, 2020
                multi day (same year): Jan 5 - Feb 6, 2020
                multi day (diff year): Jan 5, 2020 - Jan 6, 2021

            Non-all day events (startDateTime & endDateTime):
                Same as above but appends ". 8:00pm" for start time and " - 9:00pm" for end time
    */
    const singleDayFormat = 'EEEE, MMMM d, y';
    const multiDayFormatSameMonthYear = {
        start: 'MMMM ',
        both: 'd',
        end: ', y'
    };
    const multiDayFormatSameYear = {
        start: '',
        both: 'MMMM d',
        end: ', y'
    };
    const multiDayFormatDiffYear = 'MMMM d, y';
    let timeFormat = '. h:mm a';
    const endTimeFormat = ' - h:mm a';

    let start, end, format;
     if (event.startDate) {
         start = DateTime.fromISO(event.startDate, {zone: 'UTC'});
         end = DateTime.fromISO(event.endDate, {zone: 'UTC'});
    }
    else if (event.startDatetime) {
        start = DateTime.fromISO(event.startDatetime , {zone: 'UTC'}).setZone(vars.pubcalendar.timezone);
        end = DateTime.fromISO(event.endDatetime, {zone: 'UTC'}).setZone(vars.pubcalendar.timezone);
    }

    // Determine format based on event duration
    if (start.hasSame(end, 'day')) {
        // single day
        format = start.toFormat(singleDayFormat);
    }
    else if(start.hasSame(end, 'month') && start.hasSame(end, 'year')) {
        // Multi day (same month/year)
        const interval = Interval.fromDateTimes(start, end);
        format = start.toFormat(multiDayFormatSameMonthYear.start) + interval.toFormat(multiDayFormatSameMonthYear.both) + end.toFormat(multiDayFormatSameMonthYear.end);
    }
    else if(start.hasSame(end, 'year')) {
        // Multi day (same year)
        const interval = Interval.fromDateTimes(start, end);
        format = start.toFormat(multiDayFormatSameYear.start) + interval.toFormat(multiDayFormatSameYear.both) + end.toFormat(multiDayFormatSameYear.end);
    }
    else {
        // multi day (diff years)
        const interval = Interval.fromDateTimes(start, end);
        format = interval.toFormat(multiDayFormatDiffYear);
    }

    // Add time
    if (event.startDatetime) {
        if(includeBR) timeFormat = timeFormat.replace('.', '<br>');
        format += start.toFormat(timeFormat);
        if (event.endDatetime && event.startDatetime !== event.endDatetime) {
            format += end.toFormat(endTimeFormat);
            format += ` ${vars.pubcalendar.timezoneLabel ? `(${vars.pubcalendar.timezoneLabel})` : ''}`;
        }
    }

    return format;
}

export function formatLinks(str){
    if(typeof str !== 'string') return str;
    // matches absolute URLs not already wrapped in anchor tags and wraps them
    str = str.replace(/(^\s*|[^">\s]\s+)(http[s]{0,1}:\/\/[^\s"<]+)([^\s"<]|$)/gi, '$1<a href="$2$3" target="_blank">$2$3</a>');
    // matches anchor tags with 'zoom.' in the link text and replaces with Zoom
    str = str.replace(/(>)([^<]*zoom\.[^<]+)(<\/a>)/gi, `$1${lang.Zoom}$3`);
    return str;
}

export function formatLocation(location, makeLink = false){
    if(typeof location !== 'string') return location;
    location = location.trim();
    const textWithURLPattern = /(.*?)(^\s*|[^">\s]\s+)(http[s]{0,1}:\/\/[^\s"<]+)([^\s"<]|$)(.*)/i;
    // when makeLink is true and value contains text in addition to an absolute URL return an anchor tag pointing to the first absolute URL matched with link text set to Location Info or Zoom (when 'zoom.' is in value)
    if (makeLink && textWithURLPattern.test(location)) return location.replace(textWithURLPattern, `<a href="$3$4" target="_blank">${location.indexOf('zoom.') > -1 ? lang.Zoom : lang.LocationInfo}</a>`);
    // if value is an absolute url and contains 'zoom.' then set text to Zoom. Optionally make into link.
    location = location.replace(/^(http[s]{0,1}:\/\/[^\s"<]+zoom\.[^\s"<]+)$/, makeLink ? `<a href="$1" target="_blank">${lang.Zoom}</a>` : lang.Zoom);
    // if value is an absolute url then set text to empty string. Optionally make into link with 'Location Info' as link text.
    location = location.replace(/^(http[s]{0,1}:\/\/[^\s"<]+)$/, makeLink ? `<a href="$1" target="_blank">${lang.LocationInfo}</a>` : '');
    return location;
}

// Below methods currently only utilized for Event Series Links. Can be expanded as necessary
export function formatDate(date) {
    return DateTime.fromISO(date, {zone: 'UTC'});
}

export function formatDateTime(date) {
    return DateTime.fromISO(date, {zone: 'UTC'}).setZone(vars.pubcalendar.timezone);
}

export function calculateDuration(model) {
    let start, end;
    if (model.startDate) {
        start = formatDate(model.startDate);
        end = formatDate(model.endDate);
    }
    else if (model.startDatetime) {
        start = formatDateTime(model.startDatetime);
        end = formatDateTime(model.endDatetime);
    }
    const duration = end.diff(start, ['years', 'months', 'days', 'hours', 'minutes']);
    return duration;
}

export function makeEndDate(model, instance, duration) {
    let start;
    if (model.startDate) {
        start = formatDate(instance);
    }
    else if (model.startDatetime) {
        start = formatDateTime(instance);
    }
    return start.plus(duration);
}